import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listingsAPI, offersAPI } from '../api/client'
import ModalCloseButton from './ModalCloseButton'
import OfferChatPanel from './OfferChatPanel'

export interface OfferDetail {
  id: number
  listing_id: number
  buyer_id: number
  offered_price: number
  message?: string | null
  is_accepted: boolean
  is_rejected: boolean
  created_at: string
  listing_title?: string | null
  listing_price?: number | null
  offer_kind?: string
  exchange_offered_listing_id?: number | null
  exchange_extra_cash?: number | null
  exchange_note?: string | null
  exchange_offered_listing_title?: string | null
  exchange_offered_listing_price?: number | null
  exchange_offered_listing_model?: string | null
  exchange_route?: 'warehouse' | 'direct' | null
}

interface OfferDetailModalProps {
  offerId: number | null
  token: string
  role: 'seller' | 'buyer'
  onClose: () => void
  onUpdated?: () => void
  onGoToDeals?: () => void
}

export default function OfferDetailModal({
  offerId,
  token,
  role,
  onClose,
  onUpdated,
  onGoToDeals,
}: OfferDetailModalProps) {
  const { t } = useTranslation()
  const [offer, setOffer] = useState<OfferDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [showRejectForm, setShowRejectForm] = useState(false)
  const [rockConfirmed, setRockConfirmed] = useState(false)
  const [listingSnap, setListingSnap] = useState<{
    is_reserved?: boolean
    reserved_for_user_id?: number | null
    is_sold?: boolean
  } | null>(null)

  useEffect(() => {
    if (!offerId) {
      setOffer(null)
      setListingSnap(null)
      return
    }
    setLoading(true)
    setShowRejectForm(false)
    setFeedback('')
    setRockConfirmed(false)
    offersAPI
      .getOffer(offerId, token)
      .then((res) => setOffer(res.data as OfferDetail))
      .catch(() => setOffer(null))
      .finally(() => setLoading(false))
  }, [offerId, token])

  useEffect(() => {
    if (!offer) {
      setListingSnap(null)
      return
    }
    listingsAPI
      .getListing(offer.listing_id)
      .then((res) => {
        const row = res.data as {
          is_reserved?: boolean
          reserved_for_user_id?: number | null
          is_sold?: boolean
        }
        setListingSnap(row)
      })
      .catch(() => setListingSnap(null))
  }, [offer])

  if (!offerId) return null

  const isExchange = offer?.offer_kind === 'exchange'
  const isWarehouseExchange = isExchange && (offer?.exchange_route ?? 'direct') === 'warehouse'
  const pending = offer && !offer.is_accepted && !offer.is_rejected

  function accept() {
    if (!offer) return
    if (isWarehouseExchange && !rockConfirmed) {
      alert(t('exchange.rockRequired'))
      return
    }
    setBusy(true)
    offersAPI
      .acceptOffer(offer.id, token)
      .then(() => {
        onUpdated?.()
        onClose()
        onGoToDeals?.()
      })
      .catch((err) => alert(err.response?.data?.detail || err.message))
      .finally(() => setBusy(false))
  }

  function reject() {
    if (!offer) return
    setBusy(true)
    offersAPI
      .rejectOffer(offer.id, token, feedback.trim() || undefined)
      .then(() => {
        onUpdated?.()
        onClose()
      })
      .catch((err) => alert(err.response?.data?.detail || err.message))
      .finally(() => setBusy(false))
  }

  const reservedForThisBuyer =
    Boolean(listingSnap?.is_reserved) && listingSnap?.reserved_for_user_id === offer?.buyer_id

  function toggleReservation() {
    if (!offer || listingSnap?.is_sold) return
    const next = !reservedForThisBuyer
    setBusy(true)
    listingsAPI
      .setListingReservation(
        offer.listing_id,
        { reserved: next, buyer_id: next ? offer.buyer_id : null },
        token,
      )
      .then((res) => {
        const row = res.data as {
          is_reserved?: boolean
          reserved_for_user_id?: number | null
          is_sold?: boolean
        }
        setListingSnap(row)
        onUpdated?.()
      })
      .catch((err) => alert(err.response?.data?.detail || err.message))
      .finally(() => setBusy(false))
  }

  return (
    <div className="modal-overlay offer-detail-overlay" onClick={onClose}>
      <div className="modal-content offer-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="offer-detail-modal__head">
          <h3>{isExchange ? t('offerDetail.exchangeTitle') : t('offerDetail.cashTitle')}</h3>
          <ModalCloseButton onClick={onClose} aria-label={t('listing.detailClose', 'Uždaryti')} />
        </div>

        <div className="offer-detail-modal__body">
          {loading && <p className="meta-text offer-detail-loading">{t('common.loading')}</p>}
          {!loading && !offer && <p className="error-text offer-detail-loading">{t('offerDetail.loadFailed')}</p>}

          {!loading && offer && (
            <>
              <div className="offer-detail-target">
                <span className="offer-detail-target__label">{t('offerDetail.forListing')}</span>
                <strong>{offer.listing_title || `#${offer.listing_id}`}</strong>
                {offer.listing_price != null && (
                  <span className="offer-detail-target__ask">€{offer.listing_price.toFixed(2)}</span>
                )}
              </div>

              {isExchange ? (
                <div className="offer-detail-exchange">
                  <div className="offer-detail-badge offer-detail-badge--exchange">{t('offerDetail.exchangeBadge')}</div>
                  <p className="meta-text offer-detail-route">
                    {isWarehouseExchange ? t('exchange.routeWarehouse') : t('exchange.routeDirect')}
                  </p>
                  <div className="offer-detail-exchange-grid">
                    <div className="offer-detail-card">
                      <span className="offer-detail-card__label">{t('offerDetail.offeredPhone')}</span>
                      <strong>
                        {offer.exchange_offered_listing_model ||
                          offer.exchange_offered_listing_title ||
                          `#${offer.exchange_offered_listing_id}`}
                      </strong>
                      {offer.exchange_offered_listing_price != null && (
                        <span className="offer-detail-card__price">
                          €{offer.exchange_offered_listing_price.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <div className="offer-detail-card">
                      <span className="offer-detail-card__label">{t('offerDetail.extraCash')}</span>
                      <strong>€{(offer.exchange_extra_cash ?? 0).toFixed(2)}</strong>
                    </div>
                    <div className="offer-detail-card offer-detail-card--total">
                      <span className="offer-detail-card__label">{t('offerDetail.totalValue')}</span>
                      <strong>€{offer.offered_price.toFixed(2)}</strong>
                    </div>
                  </div>
                  {offer.exchange_note && (
                    <div className="offer-detail-note">
                      <span className="offer-detail-note__label">{t('offerDetail.buyerNote')}</span>
                      <p>{offer.exchange_note}</p>
                    </div>
                  )}
                  {role === 'seller' && pending && isWarehouseExchange && (
                    <label className="offer-detail-rock">
                      <input
                        type="checkbox"
                        checked={rockConfirmed}
                        onChange={(e) => setRockConfirmed(e.target.checked)}
                      />
                      <span>{t('exchange.rockAcceptLabel')}</span>
                    </label>
                  )}
                </div>
              ) : (
                <div className="offer-detail-cash">
                  <div className="offer-detail-price-row">
                    <span>{t('offerDetail.offeredPrice')}</span>
                    <strong className="offer-detail-price">€{offer.offered_price.toFixed(2)}</strong>
                  </div>
                  {offer.listing_price != null && offer.listing_price > 0 && (
                    <p className="meta-text offer-detail-discount">
                      {((1 - offer.offered_price / offer.listing_price) * 100).toFixed(0)}% {t('seller.vsAsk')}
                    </p>
                  )}
                </div>
              )}

              <p className="meta-text offer-detail-meta">
                {role === 'seller'
                  ? `${t('seller.buyer')} #${offer.buyer_id}`
                  : t('offerDetail.yourOffer')}{' '}
                · {new Date(offer.created_at).toLocaleString()}
              </p>

              {offer.is_accepted && (
                <div className="offer-detail-status offer-detail-status--ok">{t('seller.accepted')}</div>
              )}
              {offer.is_rejected && !offer.is_accepted && (
                <div className="offer-detail-status offer-detail-status--bad">{t('seller.passed')}</div>
              )}
              {role === 'seller' && listingSnap?.is_reserved && !listingSnap.is_sold ? (
                <div className="offer-detail-status offer-detail-status--reserved">
                  {reservedForThisBuyer
                    ? `${t('listing.reservedBadge')} · ${t('seller.buyer')} #${offer.buyer_id}`
                    : t('listing.detailReservedOther')}
                </div>
              ) : null}

              <OfferChatPanel
                offerId={offer.id}
                token={token}
                role={role}
                seedNote={isExchange ? offer.exchange_note : offer.message}
                disabled={offer.is_rejected && role === 'buyer'}
              />

              {role === 'seller' && pending && showRejectForm && (
                <div className="offer-detail-reject">
                  <label className="offer-detail-reject__label">
                    {t('offerDetail.rejectFeedback')}
                    <textarea
                      rows={3}
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder={t('offerDetail.rejectFeedbackPh')}
                    />
                  </label>
                </div>
              )}
            </>
          )}
        </div>

        {!loading && offer && (
          <div className="offer-detail-modal__footer">
            {role === 'seller' && pending && !showRejectForm && (
              <div className="offer-detail-actions offer-detail-actions--stack">
                <button type="button" className="btn-primary" disabled={busy || (isWarehouseExchange && !rockConfirmed)} onClick={accept}>
                  {busy ? '…' : isExchange ? t('offerDetail.acceptExchange') : t('seller.acceptOffer')}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={busy}
                  onClick={() => setShowRejectForm(true)}
                >
                  {t('seller.rejectOffer')}
                </button>
                {!listingSnap?.is_sold ? (
                  <button type="button" className="btn-secondary btn-reserve-listing" disabled={busy} onClick={toggleReservation}>
                    {busy ? '…' : reservedForThisBuyer ? t('listing.unreserve') : t('listing.reserveForBuyer')}
                  </button>
                ) : null}
              </div>
            )}

            {role === 'seller' && pending && showRejectForm && (
              <div className="offer-detail-actions">
                <button type="button" className="btn-secondary" disabled={busy} onClick={() => setShowRejectForm(false)}>
                  {t('common.cancel')}
                </button>
                <button type="button" className="btn-reject-offer" disabled={busy} onClick={reject}>
                  {busy ? '…' : t('offerDetail.confirmReject')}
                </button>
              </div>
            )}

            {role === 'buyer' && offer.is_accepted && (
              <div className="offer-detail-actions">
                <button type="button" className="btn-primary" onClick={() => { onClose(); onGoToDeals?.() }}>
                  {t('offerDetail.goToDeals')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
